import { AvatarBlock, Button, Heading, Spacer, Steps } from '@ksyos/design-system';
import { useBlocker, useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { flatten, omit } from 'lodash';
import { useEffect, useReducer, useState } from 'react';
import type { EpisodeCategory } from '@common/enums/episodeCategory';
import type { IAddableServiceGroupServiceRights } from '@common/interfaces/addableServiceGroupServiceRights';
import type {
    AddCarePathServiceSettingsType,
    IAddCarePathServiceConsumerSettings,
    IAddCarePathServiceSupervisorSettings,
} from '@common/interfaces/addCarePathServicesSettings';
import type { IAddRightsStateValidated } from '@common/validation/addCarePath';
import { ManagerAuthorization } from '@common/enums';
import { ServiceType } from '@common/enums/serviceType';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { addRightsSchema, addRightsSettingsSchema } from '@common/validation/addCarePath';
import { addCarePathSteps } from '@frontend/components/interfaces/careProviderAddCarePathState';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, scrollToValidationError } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { initializeServiceOrganizationSettings } from '@frontend/utils/initializeServiceOrganizationSettings';
import { emptyValidationErrors, validate } from '@frontend/utils/validation';
import type { ICareProviderAddRightsState, ProviderService } from '../../../interfaces/careProviderAddRightsState';
import type { IEmployment } from '../../../interfaces/employment';
import type { ServiceWithSettings } from './interfaces/serviceWithSettings';
import { ConfirmationModal } from '../../../elements/modals/ConfirmationModal';
import { RightButtons } from '../../../elements/RightButtons';
import {
    AddRightsSteps as AddRightsStepsType,
    ChooseRightsSteps,
} from '../../../interfaces/careProviderAddRightsState';
import { OverlayTemplate } from '../../../templates/OverlayTemplate';
import { CareProviderAddRightsContext } from './careProviderAddRightsContext';
import { careProviderAddRightsReducer } from './careProviderAddRightsReducer';
import { AddRightsSteps } from './steps/AddRightsSteps';
import { validateOneAddRights } from './utils/validateOne';

type NextOrSave = 'next' | 'save';

export function PersonalSettingsAddRights() {
    assertHasAuthorization(ManagerAuthorization.employmentSettingsEdit);

    const navigate = useNavigate();
    const { employmentId, episodeCategory } = useParams() as { employmentId: string; episodeCategory: EpisodeCategory };
    const [searchParams] = useSearchParams();
    const serviceGroupId = searchParams.get('serviceGroupId');
    const abortableFetch = useAbortableFetch();

    const initialState: ICareProviderAddRightsState = {
        chooseRightsStep: ChooseRightsSteps.chooseRight,
        consumerServices: [],
        currentStep: AddRightsStepsType.addRights,
        episodeCategory: episodeCategory,
        errors: emptyValidationErrors,
        providerServices: [],
        serviceEmploymentActivities: [],
        serviceOrganizationSettings: [],
        supervisorServices: [],
    };

    const [addRightsState, dispatchAddRightsAction] = useReducer(careProviderAddRightsReducer, initialState);
    const [nextAddRightsStepOrSave, setNextAddRightsStepOrSave] = useState<NextOrSave>('save');

    const [addableServiceGroupServiceRights, setAddableServiceGroupServiceRights] = useState<
        IAddableServiceGroupServiceRights | undefined
    >();
    const [serviceGroupName, setServiceGroupName] = useState('');
    const returnSearchParams = new URLSearchParams({
        serviceGroupId: serviceGroupId ?? '',
    });
    const defaultReturnUrl = `/employments/${employmentId}/personalSettings/carePaths/${episodeCategory}?${returnSearchParams.toString()}`;
    const [isSaving, setIsSaving] = useState(false);
    const blocker = useBlocker(() => {
        if (isSaving) {
            return false;
        }

        return true;
    });

    useEffect(() => {
        load();
    }, [employmentId]);

    useEffect(() => {
        loadAddableServiceRights();
    }, [serviceGroupId, employmentId]);

    useEffect(() => {
        setNextAddRightsStep();
    }, [addRightsState.currentStep, addRightsState.serviceOrganizationSettings.length]);

    useEffect(() => {
        if ((addRightsState.servicesMissingPreferredServiceConsumers ?? []).length > 0) {
            scrollToValidationError(false);
        }
    }, [addRightsState.servicesMissingPreferredServiceConsumers]);

    useEffect(() => {
        checkMissingServiceOrganizationSettings();
    }, [
        addRightsState.consumerServices.length,
        addRightsState.providerServices.length,
        addRightsState.supervisorServices.length,
        addRightsState.serviceEmploymentActivities.length,
    ]);

    if (
        !addRightsState.practitioner ||
        !addRightsState.organization ||
        !addRightsState.employment ||
        !addRightsState.episodeCategory
    ) {
        return null;
    }

    return (
        <CareProviderAddRightsContext.Provider value={{ addRightsState, dispatchAddRightsAction }}>
            <OverlayTemplate
                leftButtons={
                    <Button
                        onClick={() => navigate(defaultReturnUrl)}
                        variant="outline"
                    >
                        {translateKey('common.cancelling')}
                    </Button>
                }
                rightButtons={renderRightButtons()}
            >
                <Spacer spacing="large">
                    {renderHeader()}
                    <AvatarBlock
                        backgroundColor="white"
                        icon="practitioner-nogender"
                        imageUrl={addRightsState.practitioner.profilePictureUrl}
                        initials={getDisplayInitials(addRightsState.practitioner)}
                        size="medium"
                        subtitle={addRightsState.organization.name}
                        title={getDisplayName(addRightsState.practitioner, false, 'nameGivenNameFamily')}
                    />
                </Spacer>
                {addRightsState.currentStep === AddRightsStepsType.organizationSettings && (
                    <Steps
                        activeStep={addRightsState.currentStep}
                        sx={{ mb: 6 }}
                    >
                        {addCarePathSteps.map((step, index) => (
                            <Steps.Step
                                key={step.title}
                                label={step.title}
                                onClick={() => handleBreadCrumbSteps(index)}
                            />
                        ))}
                    </Steps>
                )}
                <AddRightsSteps
                    addableServiceGroupServiceRights={addableServiceGroupServiceRights}
                    currentStep={addRightsState.currentStep}
                    employment={addRightsState.employment}
                    onChange={handleChangeSettings}
                />
            </OverlayTemplate>

            <ConfirmationModal
                handleNo={() => blocker.reset?.()}
                handleYes={() => {
                    blocker.proceed?.();
                }}
                isOpen={blocker.state === 'blocked'}
                question={translateKey('episodeCategory.confirmCancelMessage')}
                title={translateKey('episodeCategory.confirmCancelTitle')}
            />
        </CareProviderAddRightsContext.Provider>
    );

    function renderHeader() {
        const text = translateKey('personalSettings.addRights', serviceGroupName);

        return <Heading size="xlarge">{text}</Heading>;
    }

    function renderRightButtons() {
        return (
            <RightButtons
                currentStep={addRightsState.currentStep}
                isDisabled={isRightButtonDisabled()}
                nextStepLabel={
                    nextAddRightsStepOrSave === 'save'
                        ? translateKey('personalSettings.save')
                        : translateKey('common.nextStep')
                }
                onClickNextStep={handleClickNextStep}
                onClickPreviousStep={goToPreviousStep}
            />
        );
    }

    function isRightButtonDisabled() {
        return (
            addRightsState.consumerServices.length === 0 &&
            addRightsState.providerServices.length === 0 &&
            addRightsState.supervisorServices.length === 0 &&
            addRightsState.serviceEmploymentActivities.length === 0 &&
            !addRightsState.addServiceReader
        );
    }

    function handleClickNextStep() {
        const validatedState = validateState();

        if (!validatedState || hasMissingPreferredServiceConsumers()) {
            return;
        }

        if (nextAddRightsStepOrSave === 'save') {
            onSave(validatedState);
        } else {
            goToNextStep();
        }
    }

    function goToNextStep() {
        dispatchAddRightsAction({
            currentStep: AddRightsStepsType.organizationSettings,
            type: 'setCurrentStep',
        });
    }

    function goToPreviousStep() {
        dispatchAddRightsAction({
            currentStep: AddRightsStepsType.addRights,
            type: 'setCurrentStep',
        });
    }

    function handleBreadCrumbSteps(step: number) {
        dispatchAddRightsAction({
            currentStep: step,
            type: 'setCurrentStep',
        });
    }

    async function onSave(validatedState: IAddRightsStateValidated) {
        setIsSaving(true);
        if (validatedState.serviceEmploymentActivitiesSettings.length > 0) {
            const serviceActivityAuthorizationsToSave = {
                serviceEmploymentActivitiesSettings: validatedState.serviceEmploymentActivitiesSettings,
                serviceOrganizationSettings: validatedState.serviceOrganizationSettings,
            };

            await abortableFetch.fetchJson(
                `/api/employments/${validatedState.employment.id}/addServiceActivityAuthorizations`,
                'POST',
                serviceActivityAuthorizationsToSave
            );
        } else {
            await abortableFetch.fetchJson(
                `/api/employments/${validatedState.employment.id}/addServiceRights`,
                'POST',
                validatedState
            );
        }

        navigate(defaultReturnUrl, { replace: true });
        setIsSaving(false);
    }

    function getAllSettings() {
        return {
            addServiceReader: Boolean(addRightsState.addServiceReader),
            employment: addRightsState.employment,
            episodeCategory: addRightsState.episodeCategory,
            preferredServiceOrganizationConsumers: flatten(
                addRightsState.providerServices
                    .map((item) => preparePreferredServiceOrganizationConsumers(item))
                    .filter(Boolean)
            ),
            serviceConsumerSettings: addRightsState.consumerServices.map((item) => prepareSettingsObject(item)),
            serviceEmploymentActivitiesSettings: addRightsState.serviceEmploymentActivities,
            serviceOrganizationSettings: addRightsState.serviceOrganizationSettings,
            serviceProviderSettings: addRightsState.providerServices.map((item) => prepareSettingsObject(item)),
            serviceSupervisorSettings: addRightsState.supervisorServices.map((item) => prepareSettingsObject(item)),
        };
    }

    function getSchema() {
        return nextAddRightsStepOrSave === 'save' ? addRightsSchema : addRightsSettingsSchema;
    }

    function validateState() {
        const allSettings = getAllSettings();
        const schema = getSchema();
        const { errors, item: validatedSettings } = validate(schema, allSettings);
        const allValidationErrors = errors.merge(validateIfOneIsTrue(addRightsState.providerServices));

        dispatchAddRightsAction({
            errors: allValidationErrors,
            type: 'setErrors',
        });
        if (allValidationErrors.size > 0) {
            console.warn('AddRightsValidationErrors:', allValidationErrors);
        }

        return allValidationErrors.size > 0 ? false : (validatedSettings as IAddRightsStateValidated);
    }

    function handleChangeSettings(settings: AddCarePathServiceSettingsType, errorPath?: string) {
        if (errorPath) {
            validateOneAddRights(getSchema(), settings, errorPath, addRightsState.errors, dispatchAddRightsAction);
        }
    }

    function preparePreferredServiceOrganizationConsumers(providerService: ProviderService) {
        if (providerService.preferredServiceOrganizationConsumers) {
            return providerService.preferredServiceOrganizationConsumers.map((organizationId) => ({
                organizationId,
                serviceId: providerService.id,
            }));
        }
    }

    function prepareSettingsObject(
        service:
            | ProviderService
            | ServiceWithSettings<IAddCarePathServiceConsumerSettings>
            | ServiceWithSettings<
                  IAddCarePathServiceSupervisorSettings & {
                      id: string;
                  }
              >
    ) {
        return {
            ...service.settings,
            service: {
                code: service.code,
                id: service.id,
                name: service.name,
                serviceType: service.serviceType,
            },
        };
    }

    function validateIfOneIsTrue(services: ProviderService[]) {
        let errors = emptyValidationErrors;
        const oneMustBeTrueProperties = ['isAnalyst', 'isBiometrician', 'isGrader'];
        const analystOrBiometricianOrGrader = (providerService: ProviderService) =>
            oneMustBeTrueProperties.some((key) => {
                return providerService.settings[key as 'isAnalyst' | 'isBiometrician' | 'isGrader'];
            });
        for (const [index, providerService] of services.entries()) {
            if (
                providerService.serviceType === ServiceType.examination &&
                !analystOrBiometricianOrGrader(providerService)
            ) {
                errors = errors.set(`serviceProviderSettings.${index}.analystOrBiometricianOrGrader`, 'any_required');
            }
        }

        return errors;
    }

    async function setNextAddRightsStep() {
        let addRightsNextStep: NextOrSave = 'save';

        if (
            addRightsState.currentStep === AddRightsStepsType.addRights &&
            addRightsState.serviceOrganizationSettings.length > 0
        ) {
            addRightsNextStep = 'next';
        }

        setNextAddRightsStepOrSave(addRightsNextStep);
    }

    function hasMissingPreferredServiceConsumers() {
        const servicesMissingPreferredServiceConsumers = addRightsState.providerServices
            .filter(
                (service) =>
                    service.settings.isServiceConsumerSelector &&
                    (service.preferredServiceOrganizationConsumers ?? []).length === 0
            )
            .map((service) => service.id);

        dispatchAddRightsAction({
            servicesMissingPreferredServiceConsumers,
            type: 'setServicesMissingPreferredServiceConsumers',
        });

        return servicesMissingPreferredServiceConsumers.length > 0;
    }

    async function checkMissingServiceOrganizationSettings() {
        if (!addRightsState.employment) {
            return;
        }

        const { organizationId } = addRightsState.employment;

        if (addRightsState.serviceEmploymentActivities.length) {
            await AddServiceOrganizationWithActivitiesToState(organizationId);
        } else {
            const services = [
                ...addRightsState.providerServices,
                ...addRightsState.supervisorServices,
                ...addRightsState.consumerServices,
            ];

            for (const service of services) {
                const hasServiceOrganizationSettings =
                    addRightsState.serviceOrganizationSettings.some((sos) => sos.serviceId === service.id) ||
                    (await getServiceIdsWithoutServiceOrganizationSettings(organizationId, service.id));

                if (!hasServiceOrganizationSettings) {
                    dispatchAddRightsAction({
                        serviceOrganizationSettings: initializeServiceOrganizationSettings(omit(service, 'settings')),
                        type: 'updateServiceOrganizationSettings',
                    });
                }
            }
        }
    }

    async function AddServiceOrganizationWithActivitiesToState(organizationId: string) {
        const services = [...addRightsState.serviceEmploymentActivities];

        for (const service of services) {
            const missingServiceEmploymentService = (
                addableServiceGroupServiceRights?.serviceEmploymentServices ?? []
            ).find((item) => item.id === service.id);

            if (missingServiceEmploymentService) {
                const hasServiceOrganizationSettings =
                    addRightsState.serviceOrganizationSettings.some((sos) => sos.serviceId === service.id) ||
                    (await getServiceIdsWithoutServiceOrganizationSettings(organizationId, service.id));

                if (!hasServiceOrganizationSettings) {
                    dispatchAddRightsAction({
                        serviceOrganizationSettings: initializeServiceOrganizationSettings(
                            missingServiceEmploymentService
                        ),
                        type: 'updateServiceOrganizationSettings',
                    });
                }
            }
        }
    }

    async function load() {
        const employmentFetchResult = await abortableFetch.fetchJson(`/api/employments/${employmentId}`, 'GET');
        const employmentResult = employmentFetchResult.json as IEmployment;

        await Promise.all([
            loadPractitioner(employmentResult.practitionerId),
            loadOrganization(employmentResult.organizationId),
        ]);

        dispatchAddRightsAction({
            employment: employmentResult,
            type: 'setEmployment',
        });
    }

    async function loadAddableServiceRights() {
        const url = `/api/employments/${employmentId}/addableServiceRights?serviceGroupId=${serviceGroupId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        const addableServiceGroupServiceRightsResult = result.json.items as IAddableServiceGroupServiceRights[];

        setAddableServiceGroupServiceRights(addableServiceGroupServiceRightsResult[0]);

        if (addableServiceGroupServiceRightsResult.length > 0) {
            setServiceGroupName(addableServiceGroupServiceRightsResult[0].name);
        }
    }

    async function loadPractitioner(practitionerId: string) {
        const result = await abortableFetch.fetchJson(
            `/api/practitioners/${practitionerId}/?includeProfileThumbnail=true`,
            'GET'
        );

        dispatchAddRightsAction({
            practitioner: result.json,
            type: 'setPractitioner',
        });
    }

    async function loadOrganization(organizationId: string) {
        const organizationUrl = `/api/organizations/${organizationId}`;
        const organizationResult = await abortableFetch.fetchJson(organizationUrl, 'GET');

        dispatchAddRightsAction({
            organization: organizationResult.json,
            type: 'setOrganization',
        });
    }

    async function getServiceIdsWithoutServiceOrganizationSettings(organizationId: string, serviceId: string) {
        const url = `/api/serviceOrganizationSettings?organizationId=${organizationId}&serviceId=${serviceId}&isActive=true`;
        const serviceOrganizationSettingsResult = (await abortableFetch.fetchJson(url, 'GET')).json.items;

        return serviceOrganizationSettingsResult.length > 0;
    }
}
